// Helper type for CSS class strings or arrays of strings
type SlotClassDefinition = string | string[]

// Helper type for compound variant values (single or array)
type CompoundVariantValue<T> = T | T[]

interface BlogPostSlots {
	root?: SlotClassDefinition
	header?: SlotClassDefinition
	body?: SlotClassDefinition
	footer?: SlotClassDefinition
	image?: SlotClassDefinition
	title?: SlotClassDefinition
	description?: SlotClassDefinition
	authors?: SlotClassDefinition
	avatar?: SlotClassDefinition
	meta?: SlotClassDefinition
	date?: SlotClassDefinition
	badge?: SlotClassDefinition
}

interface BlogPostVariants {
	orientation?: {
		horizontal?: Partial<BlogPostSlots>
		vertical?: Partial<BlogPostSlots>
	}
	variant?: {
		outline?: Partial<BlogPostSlots>
		soft?: Partial<BlogPostSlots>
		subtle?: Partial<BlogPostSlots>
		ghost?: Partial<BlogPostSlots>
		naked?: Partial<BlogPostSlots>
	}
	to?: {
		true?: Partial<BlogPostSlots>
	}
	image?: {
		true?: Partial<BlogPostSlots> | ''
	}
}

export interface ExtendedBlogPostThemeConfig {
	slots?: Partial<BlogPostSlots>
	variants?: Partial<BlogPostVariants>
	compoundVariants?: Array<
		{
			orientation?: CompoundVariantValue<keyof NonNullable<BlogPostVariants['orientation']>>
			variant?: CompoundVariantValue<keyof NonNullable<BlogPostVariants['variant']>>
			to?: boolean
			image?: boolean
		} & {
			class: Partial<BlogPostSlots>
		}
	>
	defaultVariants?: {
		orientation?: keyof NonNullable<BlogPostVariants['orientation']>
		variant?: keyof NonNullable<BlogPostVariants['variant']>
		to?: boolean
		image?: boolean
	}
}

export default {
	slots: {
		root: 'relative group/blog-post flex flex-col rounded-3xl sm:rounded-2xl overflow-hidden',
		header: 'relative overflow-hidden aspect-[3/2] lg:aspect-[16/9] w-full pointer-events-none',
		body: 'min-w-0 flex-1 flex flex-col h-full',
		footer: '',
		image: 'object-cover w-full flex flex-grow rounded-t-xl rounded-b-xs',
		title: 'text-base h-[48px] text-pretty font-semibold text-highlighted line-clamp-2',
		description: 'mt-2 text-sm text-pretty line-clamp-4 font-regular',
		authors: 'pt-4 mt-auto flex flex-wrap gap-x-3 gap-y-1.5',
		avatar: '',
		meta: 'flex items-center gap-2 mb-2 order-last pt-4',
		date: 'text-sm',
		badge: ''
	},
	variants: { // NOTE: Only included the variants needed for the blog post component. Add more if needed.
		orientation: {
			horizontal: {
				root: 'lg:grid lg:grid-cols-2 lg:items-stretch gap-x-8',
				header: 'aspect-auto lg:h-full',
				body: 'justify-evenly px-4 pt-2 pb-4 lg:p-8',
				image: 'rounded-b-none lg:rounded-l-2xl lg:rounded-r-none lg:h-full',
				title: 'sm:text-xl sm:h-[56px] font-semibold',
				description: 'sm:text-base line-clamp-4 sm:line-clamp-6',
				meta: 'py-4 sm:pt-0 flex-1 flex-grow mb-0 items-start'
			},
			vertical: {
				root: 'flex flex-col',
				body: 'px-4 pt-2 pb-4 sm:px-8',
				header: 'p-2 rounded-t-xl rounded-b-xs'
			}
		},
		variant: {
			outline: {
				root: 'bg-default ring ring-default',
				date: 'text-muted',
				description: 'text-highlighted'
			}
		},
		to: {
			true: {
				root: [
					'transition'
				],
				image: 'transform group-hover/blog-post:scale-none transition-opacity duration-200 group-hover/blog-post:opacity-80'
			}
		},
		image: {
			true: ''
		}
	},
	compoundVariants: [
		{
			variant: 'outline',
			to: true,
			orientation: 'horizontal',
			class: {
				root: 'hover:bg-muted/50 shadow-lg hover:shadow-xl transition-all duration-200'
			}
		},
		{
			variant: 'outline',
			to: true,
			orientation: 'vertical',
			class: {
				root: 'hover:bg-muted/50 border-b-8 border-b-(--ui-bg) hover:border-b-primary shadow-card hover:shadow-lg transition-all duration-200'
			}
		}
	],
	defaultVariants: {
		variant: 'outline'
	}
} satisfies ExtendedBlogPostThemeConfig
